import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import AdminHub from './pages/AdminHub';
import GroupsPage from './pages/User/GroupsPage';
import ThreadDetailPage from './pages/User/ThreadDetailPage';
import ChannelSettingsTabRedirect from './pages/ChannelSettingsTabRedirect';
import TasksPage from './pages/TasksPage';
import ApprovalsPage from './pages/ApprovalsPage';
import Profile from './pages/Profile';
import SettingsPage from './pages/SettingsPage';
import Login from './pages/Login';
import EmailLogin from './pages/EmailLogin';
import Register from './pages/Register';
import EmailRegister from './pages/EmailRegister';
import AppsScriptAuthPage from './pages/AppsScriptAuthPage';
import SsoCallback from './pages/SsoCallback';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import GoogleChatInboxOAuthCallback from './pages/GoogleChatInboxOAuthCallback';
import Office365OAuthCallback from './pages/Office365OAuthCallback';
import Office365AdminConsentCallback from './pages/Office365AdminConsentCallback';
import GoogleDriveOAuthCallback from './pages/callback/GoogleDriveOAuthCallback';
import GoogleCalendarOAuthCallback from './pages/callback/GoogleCalendarOAuthCallback';
import OutlookCalendarOAuthCallback from './pages/callback/OutlookCalendarOAuthCallback';
import SharePointOAuthCallback from './pages/callback/SharePointOAuthCallback';
import JiraOAuthCallback from './pages/callback/JiraOAuthCallback';
import UnifiedOAuthCallback from './pages/callback/UnifiedOAuthCallback';
import NotionOAuthCallback from './pages/callback/NotionOAuthCallback';
import GithubOAuthCallback from './pages/callback/GithubOAuthCallback';
import CompanySignup from './pages/CompanySignup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Onboarding from './pages/Onboarding';
import PaymentSuccess from './pages/PaymentSuccess';
import EmailVerificationSent from './pages/EmailVerificationSent';
import EmailVerifiedSuccess from './pages/EmailVerifiedSuccess';
import Redirect from './pages/Redirect';
import AccessDenied from './components/AccessDenied';
import { USER_ROLES } from './helpers/constants';

function App() {
  return (
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <Router>
              {/* Above MUI Drawer / channel settings (≈1300–1400) so cancel/success toasts stay visible */}
              <Toaster containerStyle={{ zIndex: 20000 }} />
              <Routes>
              {/* Login route - no layout (SSO landing) */}
              <Route path="/login" element={<Login />} />
              <Route path="/emailLogin" element={<EmailLogin />} />

              {/* Register route - no layout (SSO landing) */}
              <Route path="/register" element={<Register />} />
              <Route path="/emailRegister" element={<EmailRegister />} />

              {/* Backend SSO redirect target — reads hash tokens then sends user to app */}
              <Route path="/sso-callback" element={<SsoCallback />} />
              
              {/* Apps Script Auth flow */}
              <Route path="/apps-script-auth" element={<AppsScriptAuthPage />} />
              
              {/* Company Signup route - no layout */}
              <Route path="/companysignup" element={<CompanySignup />} />

              {/* Admin-only platform: shown to non-admins who authenticate; points them to Gmail/Outlook/Google Chat instead */}
              <Route path="/adminOnly" element={<AccessDenied variant="adminOnly" />} />
              
              {/* Forgot Password route - no layout */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              {/* Reset Password route - no layout */}
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Email Verification routes - no layout */}
              <Route path="/emailVerificationSent" element={<EmailVerificationSent />} />
              <Route path="/verifyEmail/:token" element={<EmailVerifiedSuccess />} />
              
              {/* Onboarding route - with layout */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Layout headerOnly={true}>
                    <Onboarding />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Payment Success route - with layout */}
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <Layout headerOnly={true}>
                    <PaymentSuccess />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Google OAuth callback route - no layout */}
              <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />

              {/* Google Chat Inbox OAuth callback route - no layout (separate flow from email, see docs/GoogleChatInbox_DM_FE_Implementation.md) */}
              <Route path="/auth/google-chat-inbox/callback" element={<GoogleChatInboxOAuthCallback />} />

              {/* Office 365 OAuth callback route - no layout */}
              <Route path="/auth/office365/callback" element={<Office365OAuthCallback />} />
              {/* Office 365 admin consent callback - one-time approval for org (SaaS) */}
              <Route path="/auth/office365/admin-consent-callback" element={<Office365AdminConsentCallback />} />
              
              {/* Google Drive OAuth callback route - no layout */}
              <Route path="/auth/googledrive/callback" element={<GoogleDriveOAuthCallback />} />
              
              {/* Google Calendar OAuth callback route - no layout */}
              <Route path="/auth/googlecalendar/callback" element={<GoogleCalendarOAuthCallback />} />
              
              {/* Outlook Calendar OAuth callback route - no layout */}
              <Route path="/auth/outlookcalendar/callback" element={<OutlookCalendarOAuthCallback />} />
              
              {/* SharePoint OAuth callback route - no layout */}
              <Route path="/auth/sharepoint/callback" element={<SharePointOAuthCallback />} />
              
              {/* Jira OAuth callback route - no layout */}
              <Route path="/auth/jira/callback" element={<JiraOAuthCallback />} />
              
              {/* Unified OAuth callback route - no layout */}
              <Route path="/auth/unified/callback" element={<UnifiedOAuthCallback />} />

              {/* Notion PKCE OAuth callback - no layout */}
              <Route path="/auth/notion/callback" element={<NotionOAuthCallback />} />

              {/* GitHub OAuth App callback - no layout */}
              <Route path="/auth/github-oauth/callback" element={<GithubOAuthCallback />} />
              
              {/* Redirect route - no layout (public redirect resolver) */}
              <Route path="/redirect/:shortLink" element={<Redirect />} />
              
              {/* Redirect route with API path - handles /api/v1/redirect/:shortLink */}
              <Route path="/api/v1/redirect/:shortLink" element={<Redirect />} />

              {/* Protected routes with layout - platform is admin-only; regular users work in Gmail/Outlook/Google Chat */}
              <Route path="/*" element={
                <ProtectedRoute requiredRoles={[USER_ROLES.ADMIN]}>
                  <Layout>
                    <Routes>
                      
                      {/* Admin Hub - Admin only */}
                      <Route 
                        path="/adminHub" 
                        element={
                          <ProtectedRoute requiredRoles={[USER_ROLES.ADMIN]}>
                            <AdminHub />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Support Spaces Page - Regular users */}
                      <Route 
                        path="/supportSpace" 
                        element={<GroupsPage />}
                      />
                      
                      {/* Tasks Page - All users */}
                      <Route 
                        path="/tasks" 
                        element={<TasksPage />}
                      />
                      
                      {/* Approvals Page - All users */}
                      <Route 
                        path="/approvals" 
                        element={<ApprovalsPage />}
                      />
                      
                      {/* Thread Detail Page - Regular users */}
                      <Route 
                        path="/threads/:channelId/:threadId?" 
                        element={<ThreadDetailPage />}
                      />

                      {/* Channel settings deep links — e.g. /channels/:id/automation */}
                      <Route
                        path="/channels/:channelId/:tab"
                        element={<ChannelSettingsTabRedirect />}
                      />

                      {/* Profile Page - All authenticated users */}
                      <Route 
                        path="/profile" 
                        element={<Profile />}
                      />
                      
                      {/* Settings Page - All authenticated users */}
                      <Route 
                        path="/settings" 
                        element={<SettingsPage />}
                      />
                      
                      {/* Centralized session expired / 401 page (like Access Denied) */}
                      <Route 
                        path="/sessionExpired" 
                        element={<AccessDenied variant="sessionExpired" />}
                      />
                      
                      {/* Redirect root and catch all to appropriate page based on user role */}
                      <Route path="/" element={<Navigate to="/supportSpace" replace />} />
                      <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 